import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cloud, Check, Phone, MapPin, ArrowRight, Star, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePrefetch, usePrefetchOnIdle } from '@/hooks/usePrefetch';
import LiteYouTube from '@/components/LiteYouTube';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// ZIP Lead Checker Component (Integrated into Hero)
const HeroZipChecker = ({ onResults }) => {
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleCheck = async () => {
    if (!state) {
      toast.error('Please select a state');
      return;
    }
    
    if (!zip || zip.length !== 5) {
      toast.error('Please enter a valid 5-digit ZIP code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads/check-zip`, { state, zip });
      onResults(response.data);
      
      toast.success(`Found ${response.data.count?.toLocaleString() || 0} homeowners in your area!`);
      
      setTimeout(() => {
        const signupPrompt = document.getElementById('signup-prompt');
        if (signupPrompt) {
          signupPrompt.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 1000);
      
    } catch (error) {
      toast.error('Unable to check this area. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && state && zip.length === 5) {
      handleCheck();
    }
  };

  return (
    <div className="max-w-md mx-auto lg:mx-0">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border-2 border-blue-200">
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Select State
          </label>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="w-full px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none"
            style={{ minHeight: '44px' }}
          >
            <option value="">Choose your state...</option>
            {US_STATES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            ZIP Code
          </label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="Enter 5-digit ZIP"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyPress={handleKeyPress}
            className="text-base sm:text-lg py-3 sm:py-4 text-center font-semibold"
            style={{ minHeight: '44px' }}
            maxLength={5}
          />
        </div>
        <Button
          onClick={handleCheck}
          disabled={loading || !state || zip.length !== 5}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 sm:py-5 text-base sm:text-lg font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ minHeight: '44px' }}
        >
          {loading ? 'Checking...' : 'Start Building Your List — Free'}
        </Button>
        <p className="text-center text-sm text-slate-500 mt-4 leading-relaxed">
          Free preview • No credit card required
        </p>
      </div>
    </div>
  );
};

// Testimonials Component
const Testimonials = () => {
  const testimonials = [
    {
      name: "Mike R.",
      title: "Owner",
      company: "Summit Roofing",
      location: "Dallas, TX",
      quote: "We booked 7 jobs off our first list. This paid for itself in week one.",
      rating: 5
    },
    {
      name: "Angela P.",
      title: "Operations Manager",
      company: "Ironclad Exteriors",
      location: "Tampa, FL",
      quote: "It's the first lead source we've used where we actually trust the data.",
      rating: 5
    },
    {
      name: "Josh D.",
      title: "Sales Manager",
      company: "Apex Roofing",
      location: "Colorado Springs, CO",
      quote: "Being able to see storm history and property data before calling is a game changer.",
      rating: 5
    }
  ];

  return (
    <div className="bg-slate-50 py-16 sm:py-20 border-y border-slate-200">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-3">
          What Roofers Are Saying
        </h2>
        <p className="text-center text-sm text-slate-500 mb-12 max-w-2xl mx-auto">
          Results vary by market, but these represent real customer outcomes
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {testimonials.map((testimonial, idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 sm:p-8 shadow-lg border border-slate-200">
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-slate-700 mb-6 text-base sm:text-lg leading-relaxed">
                "<span className="font-bold">{testimonial.quote}</span>"
              </p>
              <div className="border-t border-slate-200 pt-4 space-y-1">
                <p className="font-bold text-slate-900">{testimonial.name}</p>
                <p className="text-sm text-slate-600">{testimonial.title}</p>
                <p className="text-sm text-slate-600">{testimonial.company}</p>
                <p className="text-sm text-slate-500">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RoofersLandingPageNew = () => {
  const navigate = useNavigate();
  const [zipResults, setZipResults] = useState(null);

  // Prefetch signup and login pages
  const signupPrefetch = usePrefetch(() => import('@/pages/SignupPage'));
  const loginPrefetch = usePrefetch(() => import('@/pages/LoginPage'));
  
  usePrefetchOnIdle([
    () => import('@/pages/SignupPage'),
    () => import('@/pages/LoginPage'),
  ], 3000);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Sticky Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" />
              <span className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">OwnerConnect</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-sm sm:text-base px-3 sm:px-4"
                style={{ minHeight: '44px' }}
                {...loginPrefetch}
              >
                Log In
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                className="bg-blue-600 hover:bg-blue-700 text-sm sm:text-base px-3 sm:px-6"
                style={{ minHeight: '44px' }}
                {...signupPrefetch}
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-slate-50 py-12 sm:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left: Headline + Bullets */}
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Stop Calling Renters and
                Disconnected Numbers
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed max-w-xl">
                Get instant access to verified homeowner lists with phone numbers, property details, and storm history. Know who to call before you ever pick up the phone.
              </p>
              
              {/* Supporting Bullets */}
              <div className="space-y-3 sm:space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0 mt-0.5 sm:mt-1" />
                  <span className="text-base sm:text-lg text-slate-700">Verified homeowners only — no renters or bad data</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0 mt-0.5 sm:mt-1" />
                  <span className="text-base sm:text-lg text-slate-700">Direct phone numbers, storm damage data, property details</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0 mt-0.5 sm:mt-1" />
                  <span className="text-base sm:text-lg text-slate-700">No ad spend, no bidding wars, no shared leads</span>
                </div>
              </div>
            </div>

            {/* Right: ZIP Code Input */}
            <div>
              <HeroZipChecker onResults={setZipResults} />
              
              {/* Results Display - Mobile Optimized */}
              {zipResults && (
                <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-5 sm:p-6 text-center">
                  <p className="text-xs sm:text-sm text-slate-600 mb-2">Available in {zipResults.state} - ZIP {zipResults.zip}</p>
                  <p className="text-4xl sm:text-5xl font-bold text-blue-600 mb-2">
                    {zipResults.count?.toLocaleString() || '0'}
                  </p>
                  {/* Mobile: Tap-accessible tooltip */}
                  <div className="block sm:hidden">
                    <div className="flex items-center justify-center gap-2">
                      <p className="text-base text-slate-700 font-medium">Verified homeowners</p>
                      <div className="group relative inline-block">
                        <button 
                          type="button"
                          className="w-5 h-5 rounded-full bg-slate-300 text-slate-700 flex items-center justify-center text-xs font-bold active:bg-slate-400 transition-colors"
                          aria-label="What does verified mean?"
                        >
                          i
                        </button>
                        <div className="invisible group-active:visible opacity-0 group-active:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-slate-900 text-white text-xs rounded-lg p-3 z-50">
                          Verified homeowner (not renter), valid phone number, ownership confirmed, property + storm data included
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                            <div className="border-4 border-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">with phone numbers ready to call</p>
                  </div>
                  {/* Desktop: Full descriptive text */}
                  <div className="hidden sm:block">
                    <p className="text-base sm:text-lg text-slate-700 font-medium leading-relaxed">
                      Verified homeowners with phone numbers,<br className="hidden lg:block" /> ownership confirmed, and property + storm data
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Real homeowner data • Instant access
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SIGNUP PROMPT SECTION */}
      <div id="signup-prompt" className="bg-blue-600 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
            Ready to Start Calling Verified Homeowners?
          </h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Sign up free. Get your first list of homeowners with phone numbers in under 60 seconds.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/signup')}
            className="bg-white text-blue-600 hover:bg-blue-50 px-8 sm:px-10 py-6 sm:py-7 text-lg sm:text-xl font-bold shadow-lg"
            style={{ minHeight: '44px' }}
            {...signupPrefetch}
          >
            Start Building Your List — Free
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2" />
          </Button>
          <p className="text-blue-100 mt-4 text-sm sm:text-base">No credit card required • See real data instantly</p>
        </div>
      </div>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* VIDEO SECTION */}
      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 leading-tight">
              See How It Works
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Watch how roofers get verified homeowner lists in under 60 seconds. Most contractors watch this before signing up.
            </p>
          </div>
          
          {/* Video */}
          <div className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl border-4 border-blue-600">
            <LiteYouTube 
              videoId="PO0CI6RmmyA"
              title="Owner Connect Demo - How to Build Roofing Leads"
              thumbnailQuality="maxresdefault"
            />
          </div>

          {/* CTA Below Video */}
          <div className="text-center mt-10">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg font-bold shadow-lg"
              style={{ minHeight: '44px' }}
              onClick={() => navigate('/signup')}
              {...signupPrefetch}
            >
              Start Building Your List — Free
            </Button>
          </div>
        </div>
      </div>

      {/* KEY BENEFITS */}
      <div className="bg-slate-50 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-3 leading-tight">
            Stop Wasting Time on Bad Data
          </h2>
          <p className="text-center text-lg sm:text-xl text-slate-600 mb-10 sm:mb-12 max-w-3xl mx-auto leading-relaxed">
            Some months you're booked solid. Other months you're staring at the phone. Here's how to keep your crews busy year-round.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-md border-l-4 border-blue-600">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 leading-tight">
                Stop calling renters and disconnected numbers
              </h3>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                Every contact is a verified homeowner with a working phone number. No more wasted calls.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-md border-l-4 border-green-600">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 leading-tight">
                Know who to call before you pick up the phone
              </h3>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                See storm damage history, roof age, home value, and credit score on every homeowner before making contact.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-md border-l-4 border-purple-600">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 leading-tight">
                Build predictable outreach instead of hoping ads work this week
              </h3>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                Create a consistent pipeline every single week. No more feast-or-famine cycles.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-md border-l-4 border-amber-600">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 leading-tight">
                Pay once—no recurring ad spend
              </h3>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                Buy your homeowner data outright. No monthly fees, no cost-per-click, no bidding wars.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-10 sm:mb-12">
            How It Works
          </h2>
          
          <div className="space-y-6 sm:space-y-8">
            <div className="flex items-start gap-4 sm:gap-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Pick Your Target Area</h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                  Choose any state, county, or ZIP code. Filter by storm damage, roof age, home value, and more.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 sm:gap-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Preview Your Homeowner List</h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                  See exactly how many verified homeowners with phone numbers match your criteria before you buy.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 sm:gap-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Download & Start Calling</h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                  Get your CSV instantly with names, addresses, phones, property data, and storm history. Start booking jobs today.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg font-bold"
              style={{ minHeight: '44px' }}
              onClick={() => navigate('/signup')}
            >
              Start Building Your List — Free
            </Button>
          </div>
        </div>
      </div>

      {/* PRICING */}
      <div className="bg-gradient-to-br from-slate-50 to-blue-50 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-3">
            Simple, One-Time Pricing
          </h2>
          <p className="text-center text-lg sm:text-xl text-slate-600 mb-10 sm:mb-12 max-w-3xl mx-auto">
            Buy your homeowner list once. No subscriptions, no recurring fees, no surprises.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 sm:p-8 hover:shadow-xl transition-shadow">
              <div className="text-center mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">Starter</h3>
                <div className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2">$1,000</div>
                <div className="text-slate-600 text-base sm:text-lg">10,000 Homeowners</div>
                <div className="text-sm text-slate-500 mt-2">10¢ per homeowner</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-slate-700">Verified homeowner data</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-slate-700">Storm history included</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-slate-700">Instant CSV download</span>
                </li>
              </ul>
              <p className="text-xs text-slate-500 mt-4 text-center">
                You own your data. No lock-in.
              </p>
            </div>

            <div className="bg-blue-600 rounded-2xl border-4 border-blue-700 p-6 sm:p-8 hover:shadow-xl transition-shadow relative transform md:scale-105">
              <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 bg-amber-400 text-slate-900 px-4 sm:px-6 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                MOST POPULAR
              </div>
              <div className="text-center mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-3">Professional</h3>
                <div className="text-4xl sm:text-5xl font-bold text-white mb-2">$2,500</div>
                <div className="text-blue-100 text-base sm:text-lg">30,000 Homeowners</div>
                <div className="text-sm text-blue-200 mt-2">8.3¢ per homeowner</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-sm sm:text-base text-white">Everything in Starter</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-sm sm:text-base text-white">Better cost per homeowner</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-sm sm:text-base text-white">Scale your outreach</span>
                </li>
              </ul>
              <Button
                size="lg"
                className="w-full mt-6 bg-white text-blue-600 hover:bg-blue-50 font-bold py-5 sm:py-6"
                style={{ minHeight: '44px' }}
                onClick={() => navigate('/signup')}
              >
                Start Building Your List — Free
              </Button>
              <p className="text-xs text-blue-100 mt-4 text-center">
                You own your data. No lock-in.
              </p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 sm:p-8 hover:shadow-xl transition-shadow">
              <div className="text-center mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">Enterprise</h3>
                <div className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2">$5,000</div>
                <div className="text-slate-600 text-base sm:text-lg">100,000 Homeowners</div>
                <div className="text-sm text-slate-500 mt-2">5¢ per homeowner</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-slate-700">Everything in Professional</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-slate-700">Best price per homeowner</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-slate-700">Multi-state coverage</span>
                </li>
              </ul>
              <p className="text-xs text-slate-500 mt-4 text-center">
                You own your data. No lock-in.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-10 sm:mb-12">
            Common Questions
          </h2>
          
          <div className="space-y-5 sm:space-y-6">
            <div className="bg-slate-50 rounded-xl p-5 sm:p-6 border border-slate-200">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">
                Are these real homeowners or just random contacts?
              </h3>
              <p className="text-base text-slate-700 leading-relaxed">
                Every contact is a verified homeowner with property ownership confirmed through public records. We filter out renters, business addresses, and disconnected numbers. You get real people who actually own the property.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-5 sm:p-6 border border-slate-200">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">
                How current is the storm damage data?
              </h3>
              <p className="text-base text-slate-700 leading-relaxed">
                We track the last 36 months of hail and wind events for every property, showing max hail size and wind speed with dates.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-5 sm:p-6 border border-slate-200">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">
                Can I filter by specific criteria?
              </h3>
              <p className="text-base text-slate-700 leading-relaxed">
                Yes. Filter by ZIP code, county, storm damage, roof age, home value, DNC status, and more. Build exactly the list you need.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-5 sm:p-6 border border-slate-200">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">
                Is this a subscription or one-time purchase?
              </h3>
              <p className="text-base text-slate-700 leading-relaxed">
                One-time purchase. Buy your list once and own it forever. No recurring fees or monthly charges.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FINAL CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Ready to Fill Your Pipeline with Verified Homeowners?
          </h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
            Join hundreds of roofing companies using OwnerConnect to keep their crews busy year-round.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/signup')}
            className="bg-white text-blue-600 hover:bg-blue-50 px-10 sm:px-12 py-6 sm:py-8 text-lg sm:text-xl font-bold shadow-2xl"
            style={{ minHeight: '44px' }}
            {...signupPrefetch}
          >
            Start Building Your List — Free
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2" />
          </Button>
          <p className="text-blue-100 mt-4 sm:mt-6 text-base sm:text-lg">
            No credit card required • Start calling homeowners in 60 seconds
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Cloud className="w-6 h-6" />
            <span className="text-lg font-bold">OwnerConnect</span>
          </div>
          <p className="text-slate-400 text-sm">
            © 2025 OwnerConnect. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoofersLandingPageNew;
